import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ClipboardList, Phone, AlertTriangle, CheckCircle2, Clock, Plus, X,
  ShieldAlert, Users, TrendingUp, Trash2, Timer, User,
} from "lucide-react";

const PRIORITY_TONE = {
  Low:      "bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700",
  Medium:   "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600",
  High:     "bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600",
  Critical: "bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-600",
};
const STATUS_TONE = {
  Open:                 "bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300",
  "In progress":        "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "Waiting on customer":"bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "Waiting on insurer": "bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300",
  Done:                 "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  Cancelled:            "bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300",
};

export default function ClaimManagerPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState({ open_tasks: 0, critical_tasks: 0, overdue_tasks: 0, calls_today: 0, open_escalations: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewCall, setShowNewCall] = useState(false);
  const [showNewEsc, setShowNewEsc] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, e, s] = await Promise.all([
        api.get("/claim-manager/tasks"),
        api.get("/claim-manager/calls"),
        api.get("/claim-manager/escalations"),
        api.get("/claim-manager/stats"),
      ]);
      setTasks(t.data);
      setCalls(c.data);
      setEscalations(e.data);
      setStats(s.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const promote = async () => {
    try {
      await api.post("/dev/set-role/claim_manager");
      toast.success("Role set to claim manager. Refreshing...");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to promote");
    }
  };

  if (user && !["claim_manager", "admin"].includes(user.role)) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 mesh-subtle" data-testid="cm-no-access">
        <div className="glass-strong rounded-2xl p-8 text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">Claim manager access required</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            This panel is for our ops team. Your role: <span className="font-semibold">{user.role}</span>.
          </p>
          <button onClick={promote} className="mt-5 btn-signature rounded-lg px-5 py-2.5 font-semibold text-sm" data-testid="cm-demo-promote">
            Try demo as claim manager
          </button>
          <p className="text-[10px] text-slate-500 mt-2">(dev-only role switch)</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] mesh-subtle py-10" data-testid="claim-manager-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] dark:text-blue-400 mb-2 flex items-center gap-2">
            <Users className="w-3 h-3" /> Claim Manager Panel
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Your ops control tower.
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Task queue, call logs, and escalations — nothing falls through the cracks.</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Stat testid="cm-stat-open" icon={ClipboardList} label="Open tasks" value={stats.open_tasks} tone="from-blue-500 to-blue-700" />
          <Stat testid="cm-stat-critical" icon={AlertTriangle} label="Critical" value={stats.critical_tasks} tone="from-rose-500 to-rose-700" />
          <Stat testid="cm-stat-overdue" icon={Timer} label="Overdue" value={stats.overdue_tasks} tone="from-orange-500 to-orange-700" />
          <Stat testid="cm-stat-calls" icon={Phone} label="Calls today" value={stats.calls_today} tone="from-emerald-500 to-emerald-700" />
          <Stat testid="cm-stat-escalations" icon={ShieldAlert} label="Open escalations" value={stats.open_escalations} tone="from-fuchsia-500 to-fuchsia-700" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1 w-fit">
          {[
            { k: "tasks", label: "Tasks", count: tasks.filter(t => !["Done","Cancelled"].includes(t.status)).length, icon: ClipboardList },
            { k: "calls", label: "Call logs", count: calls.length, icon: Phone },
            { k: "escalations", label: "Escalations", count: escalations.filter(e => e.status !== "Closed" && e.status !== "Resolved").length, icon: AlertTriangle },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                tab === t.k
                  ? "bg-[#003DA5] text-white shadow-md"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
              data-testid={`cm-tab-${t.k}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-white/20" : "bg-slate-200 dark:bg-slate-700"}`}>{t.count}</span>
            </button>
          ))}
          <div className="ml-auto pl-2">
            <button
              onClick={() => { tab === "tasks" ? setShowNewTask(true) : tab === "calls" ? setShowNewCall(true) : setShowNewEsc(true); }}
              className="btn-signature rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
              data-testid="cm-new-button"
            >
              <Plus className="w-4 h-4" /> New
            </button>
          </div>
        </div>

        {error && <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 p-4 text-sm text-rose-700 dark:text-rose-300">{error}</div>}

        {tab === "tasks" && <TaskList tasks={tasks} loading={loading} onChanged={load} />}
        {tab === "calls" && <CallList calls={calls} loading={loading} />}
        {tab === "escalations" && <EscalationList escalations={escalations} loading={loading} onChanged={load} />}
      </div>

      {showNewTask && <NewTaskModal onClose={() => setShowNewTask(false)} onSaved={() => { setShowNewTask(false); load(); }} />}
      {showNewCall && <NewCallModal onClose={() => setShowNewCall(false)} onSaved={() => { setShowNewCall(false); load(); }} />}
      {showNewEsc && <NewEscalationModal onClose={() => setShowNewEsc(false)} onSaved={() => { setShowNewEsc(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="card-premium p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-heading font-extrabold text-2xl text-slate-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}

// ─────────── Task list ───────────
function TaskList({ tasks, loading, onChanged }) {
  const changeStatus = async (task, status) => {
    try {
      await api.patch(`/claim-manager/tasks/${task.task_id}`, { status });
      toast.success(`Task → ${status}`);
      onChanged();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (task) => {
    if (!confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/claim-manager/tasks/${task.task_id}`);
      toast.success("Task deleted");
      onChanged();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (tasks.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-14 text-center" data-testid="cm-tasks-empty">
        <ClipboardList className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-slate-700 dark:text-slate-300 font-semibold">Zero tasks — you&apos;re all caught up.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {tasks.map((t) => (
        <motion.li
          key={t.task_id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:border-[#003DA5] transition-colors"
          data-testid={`cm-task-${t.task_id}`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${PRIORITY_TONE[t.priority]}`}>{t.priority}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_TONE[t.status]}`}>{t.status}</span>
                {t.claim_id && <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400">📎 {t.claim_id}</span>}
                {t.due_at && <span className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(t.due_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
              </div>
              <div className="mt-1.5 font-heading font-bold text-slate-900 dark:text-white">{t.title}</div>
              {t.notes && <div className="text-sm text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-wrap">{t.notes}</div>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <select
                value={t.status}
                onChange={(e) => changeStatus(t, e.target.value)}
                className="text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-[#003DA5] outline-none"
                data-testid={`cm-task-status-${t.task_id}`}
              >
                {["Open","In progress","Waiting on customer","Waiting on insurer","Done","Cancelled"].map(s => <option key={s}>{s}</option>)}
              </select>
              <button onClick={() => remove(t)} className="p-1.5 text-slate-400 hover:text-rose-500" aria-label="Delete" data-testid={`cm-task-delete-${t.task_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.li>
      ))}
    </ul>
  );
}

// ─────────── Call log list ───────────
function CallList({ calls, loading }) {
  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (calls.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-14 text-center" data-testid="cm-calls-empty">
        <Phone className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-slate-700 dark:text-slate-300 font-semibold">No calls logged yet.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {calls.map((c) => (
        <li key={c.call_id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4" data-testid={`cm-call-${c.call_id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-semibold text-slate-900 dark:text-white flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.customer_mobile}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{c.outcome}</span>
                {c.claim_id && <span className="text-[10px] font-mono text-slate-500">📎 {c.claim_id}</span>}
                {c.duration_sec ? <span className="text-[10px] text-slate-500">· {Math.floor(c.duration_sec/60)}m {c.duration_sec%60}s</span> : null}
              </div>
              {c.summary && <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{c.summary}</div>}
              {c.next_action && <div className="text-xs text-[#FF7A00] font-semibold mt-1">➡ {c.next_action}</div>}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">{new Date(c.at).toLocaleString("en-IN")}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─────────── Escalations ───────────
function EscalationList({ escalations, loading, onChanged }) {
  const update = async (e, status) => {
    try {
      await api.patch(`/claim-manager/escalations/${e.escalation_id}`, { status });
      toast.success(`Escalation → ${status}`);
      onChanged();
    } catch (er) { toast.error("Failed"); }
  };
  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (escalations.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-14 text-center" data-testid="cm-escalations-empty">
        <AlertTriangle className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-slate-700 dark:text-slate-300 font-semibold">No escalations. Nice job.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {escalations.map((e) => (
        <li key={e.escalation_id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4" data-testid={`cm-escalation-${e.escalation_id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${PRIORITY_TONE[e.priority]}`}>{e.priority}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{e.status}</span>
                <span className="text-[10px] font-mono text-slate-500">📎 {e.claim_id}</span>
                {e.to_team && <span className="text-[10px] text-slate-500">→ {e.to_team}</span>}
              </div>
              <div className="mt-1.5 font-heading font-bold text-slate-900 dark:text-white">{e.reason}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1"><User className="w-3 h-3" /> {e.customer_name} · {e.customer_mobile}</div>
            </div>
            <div className="flex gap-1">
              {["Acknowledged", "Resolved", "Closed"].map(s => (
                <button
                  key={s}
                  onClick={() => update(e, s)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[#003DA5] font-semibold"
                  data-testid={`cm-esc-${e.escalation_id}-${s.toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─────────── Modals ───────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white dark:bg-slate-950 rounded-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center" data-testid="cm-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </motion.div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#003DA5] focus:outline-none";

function NewTaskModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", notes: "", priority: "Medium", claim_id: "", due_at: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await api.post("/claim-manager/tasks", { ...form, claim_id: form.claim_id || null, due_at: form.due_at ? new Date(form.due_at).toISOString() : null });
      toast.success("Task created");
      onSaved();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New task" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="cm-new-task-form">
        <Field label="Title *"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cm-task-title" /></Field>
        <Field label="Notes"><textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cm-task-notes" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Priority">
            <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="cm-task-priority">
              {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Claim ID (opt)"><input className={inputCls} value={form.claim_id} onChange={(e) => setForm({ ...form, claim_id: e.target.value.toUpperCase() })} placeholder="MIC..." data-testid="cm-task-claim-id" /></Field>
          <Field label="Due date (opt)"><input type="datetime-local" className={inputCls} value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="cm-task-due" /></Field>
        </div>
        <button type="submit" disabled={saving} className="btn-signature rounded-lg px-5 py-2.5 font-semibold w-full disabled:opacity-60" data-testid="cm-task-submit">
          {saving ? "Saving…" : "Create task"}
        </button>
      </form>
    </Modal>
  );
}

function NewCallModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ customer_mobile: "", outcome: "Connected", claim_id: "", duration_sec: "", summary: "", next_action: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_mobile.match(/^\+?[\d\s-]{6,15}$/)) { toast.error("Enter a valid mobile"); return; }
    setSaving(true);
    try {
      await api.post("/claim-manager/calls", { ...form, duration_sec: form.duration_sec ? Number(form.duration_sec) : null, claim_id: form.claim_id || null });
      toast.success("Call logged");
      onSaved();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Log a call" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="cm-new-call-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer mobile *"><input className={inputCls} value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} data-testid="cm-call-mobile" /></Field>
          <Field label="Outcome">
            <select className={inputCls} value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="cm-call-outcome">
              {["Connected","Voicemail","Not reachable","Wrong number","Follow-up scheduled"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Claim ID (opt)"><input className={inputCls} value={form.claim_id} onChange={(e) => setForm({ ...form, claim_id: e.target.value.toUpperCase() })} data-testid="cm-call-claim-id" /></Field>
          <Field label="Duration (sec)"><input type="number" min="0" className={inputCls} value={form.duration_sec} onChange={(e) => setForm({ ...form, duration_sec: e.target.value })} data-testid="cm-call-duration" /></Field>
        </div>
        <Field label="Summary"><textarea rows={2} className={inputCls} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="cm-call-summary" /></Field>
        <Field label="Next action"><input className={inputCls} value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} data-testid="cm-call-next" /></Field>
        <button type="submit" disabled={saving} className="btn-signature rounded-lg px-5 py-2.5 font-semibold w-full disabled:opacity-60" data-testid="cm-call-submit">
          {saving ? "Saving…" : "Log call"}
        </button>
      </form>
    </Modal>
  );
}

function NewEscalationModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ claim_id: "", reason: "", priority: "High", to_team: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.claim_id.trim()) { toast.error("Claim ID required"); return; }
    if (!form.reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.post("/claim-manager/escalations", { ...form, to_team: form.to_team || null });
      toast.success("Escalation raised");
      onSaved();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Raise an escalation" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="cm-new-esc-form">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Claim ID *"><input className={inputCls} value={form.claim_id} onChange={(e) => setForm({ ...form, claim_id: e.target.value.toUpperCase() })} placeholder="MIC..." data-testid="cm-esc-claim-id" /></Field>
          <Field label="Priority">
            <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="cm-esc-priority">
              {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="To team">
            <select className={inputCls} value={form.to_team} onChange={(e) => setForm({ ...form, to_team: e.target.value })} data-testid="cm-esc-team">
              <option value="">—</option>
              {["insurer","survey","legal","ops-head","tech"].map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Reason *"><textarea rows={3} className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cm-esc-reason" /></Field>
        <button type="submit" disabled={saving} className="btn-signature rounded-lg px-5 py-2.5 font-semibold w-full disabled:opacity-60" data-testid="cm-esc-submit">
          {saving ? "Saving…" : "Raise escalation"}
        </button>
      </form>
    </Modal>
  );
}
